/**
 * Zod schemas for the IPFS release manifest (`version.json` schema 1) and its
 * detached signature file (`version.json.sig`).
 *
 * This module imports nothing outside `zod`. It holds no `node:*` imports and
 * no file, network, or crypto calls, so the Cloudflare Worker that serves the
 * `latest` redirect can import it directly to validate a fetched manifest
 * before trusting it. See `docs/ipfs-releases.md` for the full contract.
 */
import { z } from 'zod';

/**
 * A Content ID for a packed `packages/ui/dist` directory produced by
 * `pack.ts`. `ipfs-car` wraps a directory as UnixFS dag-pb with CID version 1
 * and raw leaves, so the root always base32-encodes to this shape — 'bafybei'
 * followed by 52 lowercase base32 characters (RFC 4648 alphabet without
 * padding, digits limited to 2-7).
 */
export const CID_V1_DAG_PB_PATTERN = /^bafybei[a-z2-7]{52}$/;

/** A 40 character lowercase hexadecimal git commit hash. */
export const FULL_COMMIT_PATTERN = /^[0-9a-f]{40}$/;

/** A 7 character short commit, with no start/end anchors — see `SEMANTIC_VERSION_FRAGMENT`. */
const SHORT_COMMIT_FRAGMENT = '[0-9a-f]{7}';

/** The 7 character short commit used inside a release id. */
export const SHORT_COMMIT_PATTERN = new RegExp(`^${SHORT_COMMIT_FRAGMENT}$`);

/**
 * A semantic version with no start/end anchors, so it can be composed into
 * larger patterns (see `releaseIdSchema`) without a stray `$...^` collision.
 */
const SEMANTIC_VERSION_FRAGMENT = String.raw`\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?`;

/** A semantic version, matching what `packages/ui/package.json` carries. */
export const SEMANTIC_VERSION_PATTERN = new RegExp(`^${SEMANTIC_VERSION_FRAGMENT}$`);

/** A lowercase, dash-safe channel name — a branch name, not a free string. */
export const CHANNEL_NAME_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;

/** 64 lowercase hexadecimal characters — a sha256 digest. */
export const SHA256_HEX_PATTERN = /^[0-9a-f]{64}$/;

export const cidV1Schema = z
  .string()
  .regex(CID_V1_DAG_PB_PATTERN, 'expected a CID version 1, dag-pb, base32 root CID');

export const channelNameSchema = z
  .string()
  .regex(CHANNEL_NAME_PATTERN, 'channel names are lowercase, start with a letter');

/**
 * A release id, `<version>+<7-char commit>` — the GitHub Release tag and the
 * key every channel and every `releases[]` entry points at.
 */
export const releaseIdSchema = z
  .string()
  .regex(
    new RegExp(`^${SEMANTIC_VERSION_FRAGMENT}\\+${SHORT_COMMIT_FRAGMENT}$`),
    'expected "<version>+<7-char commit>"',
  );

/**
 * A bare gateway host, e.g. `dweb.link` — no scheme, no `ipfs.` prefix.
 * IPFS links always put the Content ID in the SUBDOMAIN, never the path, so
 * each release's content starts from fresh browser storage rather than
 * inheriting another release's cached origin. `buildGatewayUrl` inserts the
 * `ipfs.` label and the Content ID itself.
 */
export const gatewayBaseSchema = z
  .string()
  .min(1)
  .regex(
    /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/,
    'expected a bare hostname',
  );

/** One entry in the `releases[]` history, newest first, capped at 50. */
export const releaseEntrySchema = z.object({
  id: releaseIdSchema,
  version: z.string().regex(SEMANTIC_VERSION_PATTERN),
  commit: z.string().regex(FULL_COMMIT_PATTERN),
  cid: cidV1Schema,
  publishedAt: z.string().datetime(),
  carSha256: z.string().regex(SHA256_HEX_PATTERN),
  branch: z.string().min(1),
  channel: channelNameSchema,
});

/** The detached signature carried alongside `version.json`, byte for byte. */
export const signatureFileSchema = z.object({
  keyId: z.string().min(1),
  signature: z.string().min(1), // base64
});

/**
 * The full `version.json` schema 1 manifest. `channels` maps a channel name
 * to the release id currently live on that channel; `latest` always equals
 * `channels[defaultChannel]`, and `ipfs_address` / `ipfs_gateways` always
 * describe the release that id points at — kept in the PulseChain
 * `version.json` shape so existing readers (`resolvedDomainQueryOptions`)
 * keep working unmodified if this domain is ever read the same way.
 */
export const versionManifestSchema = z
  .object({
    schema: z.literal(1),
    name: z.literal('gibs-finance-interface'),
    sequence: z.number().int().nonnegative(),
    channels: z.record(channelNameSchema, releaseIdSchema),
    latest: releaseIdSchema,
    defaultChannel: channelNameSchema,
    releases: z.array(releaseEntrySchema).max(50),
    gateways: z.array(gatewayBaseSchema),
    ipfs_address: cidV1Schema,
    ipfs_gateways: z.array(z.string().url()),
  })
  .superRefine((manifest, ctx) => {
    if (manifest.channels[manifest.defaultChannel] !== manifest.latest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'latest must equal channels[defaultChannel]',
        path: ['latest'],
      });
    }
    const latestEntry = manifest.releases.find((entry) => entry.id === manifest.latest);
    if (!latestEntry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'latest release id must exist in releases[]',
        path: ['latest'],
      });
      return;
    }
    if (latestEntry.cid !== manifest.ipfs_address) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ipfs_address must equal the cid of the latest release',
        path: ['ipfs_address'],
      });
    }
  });

export type GatewayBase = z.infer<typeof gatewayBaseSchema>;
export type ReleaseEntry = z.infer<typeof releaseEntrySchema>;
export type SignatureFile = z.infer<typeof signatureFileSchema>;
export type VersionManifest = z.infer<typeof versionManifestSchema>;

/**
 * How many of the newest releases our own IPFS node keeps pinned, and so how
 * many our gateway can serve. The pin watcher's default and the landing
 * page's release list both read it, so the page never links a release the
 * node has already unpinned. ansible's `gibs_ipfs_pin_keep_count` must match.
 */
export const DEFAULT_PIN_KEEP_COUNT = 10;

/**
 * The releases that stay pinned: the newest `keepCount` (`releases[]` is
 * newest first), plus the release every channel currently points at, so a
 * channel that has not published for a while never loses its live build.
 */
export const selectReleasesToKeep = (
  manifest: VersionManifest,
  keepCount: number,
): ReleaseEntry[] => {
  const channelReleaseIds = new Set(Object.values(manifest.channels));
  return manifest.releases.filter(
    (release, index) => index < keepCount || channelReleaseIds.has(release.id),
  );
};

/**
 * The public gateway hosts the manifest advertises unless `RELEASE_GATEWAYS`
 * overrides them. Our own gateway is not here — see `own-gateway.ts` and
 * `docs/ipfs-releases.md` for why it stays in code.
 *
 * - A gateway that refuses our releases is not listed. `4everland.io`
 *   answers 403 for them, so it is in {@link REFUSING_GATEWAYS}, not here.
 * - `inbrowser.link` is the service worker gateway that replaced
 *   `dweb.link` when that HTTP gateway retired on 2026-09-21. It renders
 *   the app in a browser; see {@link SERVICE_WORKER_GATEWAYS}.
 */
export const DEFAULT_RELEASE_GATEWAYS: GatewayBase[] = ['inbrowser.link'];

/**
 * Public gateways that answer 403 for our releases (`4everland.io`, measured
 * 2026-10-01). Older signed manifests still list them. Readers of a manifest,
 * such as the landing page, must not link them.
 */
export const REFUSING_GATEWAYS: readonly GatewayBase[] = Object.freeze(['4everland.io']);

/**
 * Public gateways that serve content through a service worker, not over
 * plain HTTP. The first visit loads a small page that installs a service
 * worker, and the worker fetches and checks the content inside the browser.
 * An HTTP client with no browser — curl, the redirect Worker's probe, the
 * post-publish check — gets a 403 from these hosts even when a browser
 * renders the release correctly (verified in Chrome for our release,
 * 2026-10-01). So nothing may probe them: a probe would always report them
 * dead. They use the same `<cid>.ipfs.<host>` form as every other gateway.
 */
export const SERVICE_WORKER_GATEWAYS: readonly GatewayBase[] = Object.freeze(['inbrowser.link']);

/**
 * Whether only a browser can open releases on this gateway.
 *
 * @param base - a bare gateway host from `gateways`
 * @returns true for a service worker gateway, which nothing may probe
 */
export const isServiceWorkerGateway = (base: GatewayBase): boolean =>
  SERVICE_WORKER_GATEWAYS.includes(base);

/**
 * Builds the full gateway URL for one Content ID from a bare gateway host.
 * The Content ID always goes in the subdomain, never the path — see
 * `gatewayBaseSchema`.
 */
export const buildGatewayUrl = (base: GatewayBase, cid: string): string =>
  `https://${cid}.ipfs.${base}/`;
